import React, { useEffect } from 'react';
import dynamic from 'next/dynamic';
import {
    CONTENT_SUB_TYPE,
    CONTENT_TYPE,
    LSTORAGE_KEY,
    PLACE_HOLDER,
} from 'src/constants';
import { LocalStore, ObjectUtils } from 'src/utils';
import { useViewTime } from 'src/hooks';
import { trackingContentDetail } from 'src/utils/tracking';
import { Element } from 'src/components';
import Gallery from 'src/components/gallery';
import { AdBanner } from 'src/modules/shared/components/ad';
import ContentText, { ITextProps } from './ContentText';
import { IImageProps } from './ContentImage';
import ContentImageSlider from './ContentImageSlider';
import { Loader } from '@zmedia/bm-common/components/loader';

import styles from './style.module.scss';

const ContentVideo = dynamic(() => import('./ContentVideo'), {
    ssr: false,
});

const ContentAudio = dynamic(() => import('./ContentAudio'), {
    ssr: false,
});

interface IProps {
    bodys: ITextProps[] | IImageProps[];
    isFetching?: boolean;
    isOpenContent?: boolean;
    contentId: number;
    publisherId?: number;
    lazyLoad?: boolean;
}

export const CONTENT_MAP = {
    [CONTENT_TYPE.TEXT]: ContentText,
    [CONTENT_TYPE.IMAGE]: ContentImageSlider,
    [CONTENT_TYPE.VIDEO]: ContentVideo,
    [CONTENT_TYPE.AUDIO]: ContentAudio,
    [CONTENT_TYPE.AD_BANNER]: AdBanner,
};

const countItems = (arr: any) =>
    arr.reduce(
        (total: number, item: any) =>
            total + 1 + (item.childs ? countItems(item.childs) - 1 : 0),
        0,
    );

export default React.memo(function ContentBody({
    isFetching = false,
    isOpenContent = false,
    bodys = [],
    contentId,
    publisherId,
    lazyLoad = true,
}: IProps) {
    let currentIndex = 0;

    useEffect(() => {
        const size = countItems(bodys);
        (window as any)._BM.currentIndex = 0;
        (window as any)._BM.bodyLength = size;
        return () => {
            setTimeout(() => {
                const data = (LocalStore.get(LSTORAGE_KEY.VIEW_DETAIL) ||
                    {}) as any;
                if (!ObjectUtils.isEmpty(data?.[contentId])) {
                    trackingContentDetail(data?.[contentId]);
                    delete data?.[contentId];
                    LocalStore.set(LSTORAGE_KEY.VIEW_DETAIL, data);
                }
            }, 100);
        };
    }, [contentId]);

    function renderItems(items: any[] = []) {
        return items.map((item, index) => {
            const size = item.childs ? item.childs.length : 1;
            return renderItem(item, index, size);
        });
    }

    function renderItem(item: any, index: number, size: number) {
        const Component = CONTENT_MAP[item?.type];

        if (!Component) {
            return null;
        }

        item.id = index;
        item.contentId = contentId;
        item.index = currentIndex;

        currentIndex = currentIndex + size;

        let _props = {
            ...item,
            isOpenContent,
            contentId,
            publisherId,
        };
        if (item.type === CONTENT_TYPE.IMAGE) {
            const _caption =
                bodys[index + 1] &&
                bodys[index + 1].subType === CONTENT_SUB_TYPE.IMAGE_CAPTION
                    ? bodys[index + 1].content
                    : '';
            _props = {
                ...item,
                caption: _caption,
                lazyLoad: lazyLoad,
            };
        } else if (item.type === CONTENT_TYPE.VIDEO) {
            _props = {
                ...item,
                contentId,
                publisherId,
            };
            return (
                <Element
                    key={`content.type.${index}${contentId}`}
                    style={{
                        width: 'calc(100vw - 32px)',
                        maxWidth: 586,
                        height: `calc((100vw - 32px) * ${
                            (item.height || 9) / (item.width || 16)
                        })`,
                        maxHeight: '80vh',
                        background: `url(${PLACE_HOLDER.VIDEO}) no-repeat center center`,
                        backgroundSize: 'cover',
                        position: 'relative',
                    }}
                >
                    <Component {..._props} />
                </Element>
            );
        }

        return <Component key={`content.type.${index}`} {..._props} />;
    }

    const { ref } = useViewTime({
        tracking: 'body',
        contentId: contentId,
        threshold: 0,
        onFinish: () => {},
    });

    if (isFetching) {
        <Element className={styles['content-body']}>
            <Loader text={'Đang tải nội dung ...'} />
        </Element>;
    }

    return (
        // @ts-ignore: Unreachable code error
        <Gallery.Gallery
            id="my-gallery"
            options={{
                shareEl: false,
                zoomEl: false,
                showAnimationDuration: 0,
                hideAnimationDuration: 10,
            }}
        >
            <Element className={styles['content-body']} domRef={ref}>
                {renderItems(bodys)}
            </Element>
        </Gallery.Gallery>
    );
});
